import {
  ActionIcon, Box, Button, Divider, Grid, Group, Image,
  List, Modal, Stack, Text, Textarea, UnstyledButton
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@dehub/theme";
import {
  ArrowLeftIcon, CaretLeftIcon, CaretRightIcon, CheckCircleIcon, CheckIcon,
  ClipboardTextIcon, ImageIcon, MinusIcon, ShieldCheckIcon, UserIcon, XCircleIcon
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi } from "../../../apis";
import type { SellerVerificationDecision } from "../../../apis/return";
import { DeliveryActionButton } from "../../../components/delivery/action-controls";
import { DotPill } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { ReasonBadge, ReturnStatusBadge } from "../../../components/return/atoms";
import { DetailCard, Field, SideRow } from "../../../components/return/detail-card";
import { useReturnActions } from "../../../components/return/return-actions";
import { TrustBadge } from "../../../components/return/trust-badge";
import { DeniedCard, ListState } from "../../../components/ui";
import { dateFormat, dateTimeSecFormat } from "../../../utils/date";
import { message } from "../../../utils/message";

type TFn = (k: string, o?: Record<string, unknown>) => string;

// CS decision cards. VERIFIED resumes the standard propose/accept flow —
// there is NO separate manager-approval step (single-step backend, §D3).
const decisions = (t: TFn): { value: SellerVerificationDecision; title: string; desc: string }[] => [
  {
    value: "VERIFIED",
    title: t("offSystem.decision.verifiedTitle"),
    desc : t("offSystem.decision.verifiedDesc"),
  },
  {
    value: "REJECTED",
    title: t("offSystem.decision.rejectedTitle"),
    desc : t("offSystem.decision.rejectedDesc"),
  }
];

// One verification-checklist row. `state` decides the leading marker:
//   ok     → backed by payload data (check)
//   manual → no backing data; the CS must judge it by hand (neutral marker)
type CheckState = "ok" | "manual";
interface CheckRow {
  label: string;
  state: CheckState;
  detail?: string;
}

export function OffSystemVerifyPage (){
  const { t } = useTranslation("returns");
  const DECISIONS = decisions(t);
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAccess } = usePermission("RETURN_OFFSYSTEM_VERIFY");
  const { stateOf } = useReturnActions();

  const { data, error, isLoading, mutate } = useSWR(
    id && hasAccess ? ["returns.get", id] : null,
    () => ReturnApi.get(id),
    { revalidateOnFocus: false }
  );

  const [decision, setDecision] = useState<SellerVerificationDecision | null>(null);
  const [notes, setNotes] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  // Zoomable evidence gallery (mirrors items-table.tsx).
  const [gallery, setGallery] = useState<string[]>([]);
  const [gIdx, setGIdx] = useState(0);
  const [galleryOpened, galleryH] = useDisclosure(false);
  const openGallery = (photos: string[], start: number) => {
    setGallery(photos);
    setGIdx(start);
    galleryH.open();
  };
  const gPrev = () => setGIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const gNext = () => setGIdx((i) => (i + 1) % gallery.length);

  const ret = data?.returnRequest;
  const isClaim = !!ret?.isOffSystemBuyerClaim;
  const isPending = ret?.sellerVerificationStatus === "PENDING";
  const notesError = showErrors && !notes.trim();
  // Permission code first, then «claim is not pending» — the matrix answers both (return-actions.ts).
  const verifyState = stateOf("offSystemVerify", ret);

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!decision) {
      showNotification({ color: "red", title: t("toast.error"), message: t("offSystem.pickDecision") });
      return;
    }
    // The notes are the audit ground and, for a rejection, the rejection reason (BE: required on REJECTED).
    if (!notes.trim()) {
      showNotification({ color: "red", title: t("toast.error"), message: t("offSystem.notesRequired") });
      return;
    }
    try {
      setBusy(true);
      await ReturnApi.verifySeller(id, { decision, notes: notes.trim() });
      showNotification({ color: "green", title: t("toast.success"), message: t("offSystem.decisionSent") });
      navigate(`/returns/${id}`);
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const breadcrumb = [
    { to: "/", label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { to: `/returns/${id}`, label: ret?.returnNumber ?? "-" },
    { label: t("actionBar.offSystemVerify") }
  ];

  // Checklist derived strictly from data actually on the payload. Items with no
  // backing field render as neutral manual-check rows (not fake checks).
  const checklist: CheckRow[] = ret ? [
    {
      label : t("offSystem.check.businessRegistered"),
      state : ret.buyerBusinessId ? "ok" : "manual",
      detail: ret.buyerBusinessName ?? t("offSystem.check.notRegistered")
    },
    {
      label : t("offSystem.check.proofOfPurchase"),
      state : ret.proofOfPurchase ? "ok" : "manual",
      detail: ret.proofOfPurchase ?? t("offSystem.check.noProof")
    },
    { label: t("offSystem.check.phoneVerified"),  state: "manual", detail: t("offSystem.check.manual") },
    { label: t("offSystem.check.emailVerified"), state: "manual", detail: t("offSystem.check.manual") },
    { label: t("offSystem.check.orderHistory"),     state: "manual", detail: t("offSystem.check.manual") },
    { label: t("offSystem.check.photoReal"),   state: "manual", detail: t("offSystem.check.photoJudge") }
  ] : [];

  const claimPhotos = ret?.evidencePhotos ?? [];

  if (!hasAccess) {
    return (
      <PageLayout title={t("offSystem.title")} breadcrumb={breadcrumb}>
        <DeniedCard permission="RETURN_OFFSYSTEM_VERIFY" />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("offSystem.title")}
      subTitle={ret ? `${ret.returnNumber} · ${ret.buyerBusinessName ?? "-"}` : undefined}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={ret ? [<ReturnStatusBadge key="status" status={ret.status} size="lg" />] : undefined}
    >
      {error && !data ? (
        <Stack gap="md" align="flex-start">
          <ListState state="error" title={t("warehouseReceipt.loadError")} onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          <Button variant="default" leftSection={<ArrowLeftIcon size={18} />} onClick={() => navigate(`/returns/${id}`)}>
            {t("warehouseReceipt.backToDetail")}
          </Button>
        </Stack>
      ) : data && ret ? (
        !isClaim || !isPending ? (
          <DetailCard title={t("offSystem.cannotVerifyTitle")}>
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                {!isClaim
                  ? t("offSystem.notOffSystem")
                  : <>{t("offSystem.alreadyDecidedLead")}{" "}
                    <Text span fw={600}>{t(`verify.${ret.sellerVerificationStatus}`, { defaultValue: "-" })}</Text> {t("offSystem.alreadyDecidedTail")}</>}
              </Text>
              {ret.sellerVerificationStatus && ret.sellerVerificationStatus !== "PENDING" ? (
                <Box>
                  <Text size="sm">
                    {t("detail.field.date")}: <Text span fw={600} ff="monospace" fz="var(--nw-fz-mono)">{ret.sellerVerifiedAt ? dateTimeSecFormat(ret.sellerVerifiedAt) : "-"}</Text>
                  </Text>
                  {ret.sellerVerificationNotes ? (
                    <Text size="sm" c="var(--nw-text-2)" mt={4}>{ret.sellerVerificationNotes}</Text>
                  ) : null}
                </Box>
              ) : null}
              <Button variant="default" leftSection={<ArrowLeftIcon size={18} />} onClick={() => navigate(`/returns/${id}`)}>
                {t("warehouseReceipt.backToDetail")}
              </Button>
            </Stack>
          </DetailCard>
        ) : (
          <Stack gap="md">
            <DetailCard title={t("offSystem.instructionsTitle")}>
              <List type="ordered" size="sm" spacing={4}>
                <List.Item>{t("offSystem.instruction1")}</List.Item>
                <List.Item>{t("offSystem.instruction2")}</List.Item>
                <List.Item>{t("offSystem.instruction3")}</List.Item>
              </List>
              <Text size="sm" c="var(--nw-text-2)" mt="sm">
                {t("offSystem.instructionNote")}
              </Text>
            </DetailCard>

            <Grid>
              {/* ── Left: buyer info, claim, evidence, decision ── */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <DetailCard title={<Group gap="xs"><UserIcon size={18} />{t("offSystem.buyerInfo")}</Group>}>
                    <SideRow label={t("detail.field.business")} value={ret.buyerBusinessName} />
                    {/* Reference code (BU-…), never the internal UUID. */}
                    <SideRow label={t("offSystem.businessId")} value={ret.buyerBusinessRefCode} mono />
                    <SideRow
                      label={t("offSystem.trustScore")}
                      value={ret.buyerBusinessId ? <TrustBadge businessId={ret.buyerBusinessId} /> : null}
                    />
                  </DetailCard>

                  <DetailCard title={<Group gap="xs"><ClipboardTextIcon size={18} />{t("offSystem.buyerClaim")}</Group>}>
                    <Stack gap="sm">
                      <Group gap="sm" wrap="wrap" align="center">
                        <ReasonBadge reason={ret.returnReason} />
                        {ret.proofOfPurchaseDate ? (
                          <Text size="sm" c="var(--nw-text-2)">{t("offSystem.proofDate")}: <Text span ff="monospace" fz="var(--nw-fz-mono)">{dateFormat(ret.proofOfPurchaseDate)}</Text></Text>
                        ) : null}
                      </Group>
                      <SideRow label={t("detail.field.proofOfPurchase")} value={ret.proofOfPurchase} />
                      {ret.reasonDetails ? (
                        <Field label={t("offSystem.descriptionLabel")} value={ret.reasonDetails} />
                      ) : (
                        <Text size="sm" c="var(--nw-text-2)">{t("offSystem.noDescription")}</Text>
                      )}

                      <Divider />

                      <Box>
                        <Text size="sm" fw={600} mb="xs">{t("transitResolve.evidencePhoto")}</Text>
                        {claimPhotos.length ? (
                          <Group gap="md" wrap="wrap">
                            {claimPhotos.map((url, i) => (
                              <Stack key={i} gap={2} align="center">
                                <UnstyledButton onClick={() => openGallery(claimPhotos, i)} aria-label={t("transitResolve.zoom")}>
                                  <Image src={url} w={120} h={90} radius="md" fit="cover" fallbackSrc="https://placehold.co/240x180?text=No+image" />
                                </UnstyledButton>
                                <Text size="sm" c="var(--nw-text-2)">{t("transitResolve.zoom")}</Text>
                              </Stack>
                            ))}
                          </Group>
                        ) : (
                          <Text size="sm" c="var(--nw-text-2)">{t("offSystem.noPhotos")}</Text>
                        )}
                      </Box>
                    </Stack>
                  </DetailCard>

                  <DetailCard title={<Group gap="xs"><CheckCircleIcon size={18} />{t("offSystem.csDecision")}</Group>}>
                    <Stack gap="md">
                      <Grid role="radiogroup" aria-label={t("offSystem.csDecision")}>
                        {DECISIONS.map((d) => {
                          const active = decision === d.value;
                          return (
                            <Grid.Col key={d.value} span={{ base: 12, sm: 6 }}>
                              <UnstyledButton
                                role="radio"
                                aria-checked={active}
                                onClick={() => setDecision(d.value)}
                                w="100%"
                                p="sm"
                                style={{
                                  border: `1px solid ${active ? "var(--mantine-primary-color-filled)" : "var(--nw-table-border)"}`,
                                  borderRadius: "var(--mantine-radius-lg)",
                                  background: active ? "var(--mantine-primary-color-light)" : undefined,
                                }}
                              >
                                <Group gap="xs" wrap="nowrap" align="flex-start">
                                  <Box mt={2}>{d.value === "VERIFIED" ? <CheckIcon size={18} /> : <XCircleIcon size={18} />}</Box>
                                  <Box>
                                    <Text size="sm" fw={600}>{d.title}</Text>
                                    <Text size="sm" c="var(--nw-text-2)">{d.desc}</Text>
                                  </Box>
                                </Group>
                              </UnstyledButton>
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                      {showErrors && !decision ? (
                        <Text size="sm" c="red">{t("offSystem.pickDecision")}</Text>
                      ) : null}

                      <Textarea
                        label={t("offSystem.csNotes")}
                        description={t("offSystem.csNotesDesc")}
                        placeholder={t("offSystem.csNotesPlaceholder")}
                        value={notes}
                        onChange={(e) => setNotes(e.currentTarget.value)}
                        autosize
                        minRows={3}
                        required
                        error={notesError ? t("offSystem.noteRequiredShort") : undefined}
                      />

                      <Group justify="space-between" align="center">
                        <Text size="sm" c="var(--nw-text-2)" maw={420}>
                          {t("offSystem.slaAutoReject")}
                        </Text>
                        <Group gap="sm">
                          <Button variant="default" onClick={() => navigate(`/returns/${id}`)} disabled={busy}>
                            {t("resolutionPanel.cancel")}
                          </Button>
                          <DeliveryActionButton
                            state={{ allowed: verifyState.enabled, reason: verifyState.reason }}
                            leftSection={<CheckCircleIcon size={18} />}
                            onClick={handleSubmit}
                            loading={busy}
                          >
                            {t("offSystem.sendDecision")}
                          </DeliveryActionButton>
                        </Group>
                      </Group>
                    </Stack>
                  </DetailCard>
                </Stack>
              </Grid.Col>

              {/* ── Right: verification checklist ── */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <DetailCard title={<Group gap="xs"><ShieldCheckIcon size={18} />{t("offSystem.checklistTitle")}</Group>}>
                  <Stack gap="sm">
                    {checklist.map((c) => (
                      <Group key={c.label} gap="sm" wrap="nowrap" align="flex-start">
                        <ActionIcon
                          component="span"
                          size={22}
                          radius="xl"
                          variant="light"
                          color={c.state === "ok" ? "green" : "gray"}
                          aria-hidden
                        >
                          {c.state === "ok" ? <CheckIcon size={14} /> : <MinusIcon size={14} />}
                        </ActionIcon>
                        <Box>
                          <Text size="sm" fw={500}>{c.label}</Text>
                          {c.detail ? <Text size="sm" c="var(--nw-text-2)">{c.detail}</Text> : null}
                        </Box>
                      </Group>
                    ))}
                    <Divider />
                    <Group gap="xs">
                      <DotPill tone="green">{t("offSystem.badgeChecked")}</DotPill>
                      <DotPill tone="grey">{t("offSystem.badgeManual")}</DotPill>
                    </Group>
                    <Text size="sm" c="var(--nw-text-2)">
                      {t("offSystem.checklistNote")}
                    </Text>
                  </Stack>
                </DetailCard>
              </Grid.Col>
            </Grid>
          </Stack>
        )
      ) : null}

      {/* Zoomable evidence gallery */}
      <Modal opened={galleryOpened} onClose={galleryH.close} size="lg" centered
        title={<Group gap="xs"><ImageIcon size={18} /><Text fw={600}>{t("transitResolve.evidencePhoto")}</Text></Group>}>
        {gallery.length ? (
          <Stack gap="sm">
            <Image src={gallery[gIdx]} radius="md" mah={440} fit="contain" fallbackSrc="https://placehold.co/600x400?text=No+image" />
            {gallery.length > 1 ? (
              <Group justify="center" gap="md">
                <ActionIcon variant="default" onClick={gPrev} aria-label={t("transitResolve.prev")}><CaretLeftIcon size={18} /></ActionIcon>
                <Text size="sm">{gIdx + 1} / {gallery.length}</Text>
                <ActionIcon variant="default" onClick={gNext} aria-label={t("transitResolve.next")}><CaretRightIcon size={18} /></ActionIcon>
              </Group>
            ) : null}
          </Stack>
        ) : null}
      </Modal>
    </PageLayout>
  );
}
